import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { HeroUIProvider } from "@heroui/react";
import { normalizeLanguage } from "./i18n/config";
import { applySeoToDocument } from "./lib/seo";
import { useTranslation } from "react-i18next";
import { CookieConsentProvider } from "./components/feature/CookieConsent";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    let frameId = 0;
    let timeoutId = 0;
    let attempts = 0;

    const scrollToHash = () => {
      const elementId = decodeURIComponent(hash.replace('#', ''));
      const element = document.getElementById(elementId);

      if (element) {
        const headerOffset = 118;
        const top = Math.max(element.getBoundingClientRect().top + window.scrollY - headerOffset, 0);
        window.scrollTo({ top, behavior: 'auto' });
        return;
      }

      attempts += 1;

      if (attempts < 24) {
        frameId = window.requestAnimationFrame(scrollToHash);
      }
    };

    timeoutId = window.setTimeout(scrollToHash, 50);

    return () => {
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(frameId);
    };
  }, [pathname, hash]);

  return null;
}

function SyncLanguageMeta() {
  useEffect(() => {
    const applyLanguage = (language: string) => {
      document.documentElement.lang = normalizeLanguage(language);
    };

    applyLanguage(i18n.resolvedLanguage || i18n.language);
    i18n.on('languageChanged', applyLanguage);

    return () => {
      i18n.off('languageChanged', applyLanguage);
    };
  }, []);

  return null;
}

function SyncSeoMeta() {
  const location = useLocation();
  const { i18n: i18nInstance } = useTranslation();

  useEffect(() => {
    const applySeo = (language?: string) => {
      applySeoToDocument(location.pathname, normalizeLanguage(language || i18nInstance.resolvedLanguage || i18nInstance.language));
    };

    applySeo();
    i18nInstance.on('languageChanged', applySeo);

    return () => {
      i18nInstance.off('languageChanged', applySeo);
    };
  }, [i18nInstance, location.pathname]);

  return null;
}

function App() {
  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="cosmic-bg pointer-events-none fixed inset-0 -z-20" />
      <div className="cosmic-stars pointer-events-none fixed inset-0 -z-10" />
      <HeroUIProvider>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter basename={__BASE_PATH__}>
            <CookieConsentProvider>
              <SyncLanguageMeta />
              <SyncSeoMeta />
              <ScrollToTop />
              <AppRoutes />
            </CookieConsentProvider>
          </BrowserRouter>
        </I18nextProvider>
      </HeroUIProvider>
    </div>
  );
}

export default App;
